import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import StockThumb from "@/components/StockThumb";
import QrScanDialog from "@/components/QrScanDialog";
import DepartmentChips, { DepartmentBadge } from "@/components/DepartmentChips";
import MaterialChips from "@/components/MaterialChips";
import SupplierChips from "@/components/SupplierChips";
import CategoryChips from "@/components/CategoryChips";
import ReceiveCelebration from "@/components/ReceiveCelebration";
import ReceiveFromPODialog from "@/components/ReceiveFromPODialog";
import { useDepartments } from "@/contexts/DepartmentsContext";
import { PackagePlus, Search, X, Plus, Minus, ScanLine, Hourglass, Truck, XCircle } from "lucide-react";
import BackorderFormDialog from "@/components/BackorderFormDialog";
import ClosePODialog from "@/components/ClosePODialog";
import { fmtQty } from "@/lib/pack";

const Receive = () => {
  const { user } = useAuth();
  const { colorFor } = useDepartments();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [material, setMaterial] = useState("all");
  const [category, setCategory] = useState("all");
  // Supplier filter (chip row) — separate from `supplier` below which is
  // the free-text input capturing WHO the delivery is from during a receive.
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [supplier, setSupplier] = useState("");
  const [batch, setBatch] = useState("");
  const [notes, setNotes] = useState("");
  const [unitCost, setUnitCost] = useState("");
  // "Include cutting fee" toggle — auto-populated when the selected
  // item has a default_processor_fee_per_log > 0. When on, the backend
  // adds `fee / 10 wheels` to the receive movement's unit_cost so
  // downstream reports show true landed cost per wheel.
  const [includeProcessorFee, setIncludeProcessorFee] = useState(true);
  // Open POs panel — sent / partially_received POs (skip companion service
  // requests since those don't have physical goods to receive) so the
  // "receiver" can click and check-off the packing slip in one flow.
  const [openPos, setOpenPos] = useState([]);
  const [receiveFromPo, setReceiveFromPo] = useState(null);
  const loadOpenPos = async () => {
    try {
      const { data } = await api.get("/po");
      setOpenPos(
        (data || []).filter(
          (p) =>
            !p.companion_of &&
            (p.status === "sent" ||
              p.status === "actioned" ||
              p.status === "partially_received"),
        ),
      );
    } catch {
      /* silent — the panel just hides if we can't load */
    }
  };
  useEffect(() => {
    loadOpenPos();
    const handler = () => loadOpenPos();
    window.addEventListener("stock-updated", handler);
    return () => window.removeEventListener("stock-updated", handler);
  }, []);

  // v180 — Unified Close PO flow (replaces v179 direct dismiss).
  // Opens ClosePODialog which handles reason selection + optional notes,
  // hits POST /po/{pid}/close on the backend. No stock/cost changes.
  const [closePo, setClosePo] = useState(null);
  // Cost-calc helper — tracks which field the user is currently typing in
  // so bidirectional syncing doesn't re-format the caret away mid-keystroke.
  // Mirrors the pattern used in StockFormDialog.
  const [costFocus, setCostFocus] = useState(null);
  const [costDraft, setCostDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [mode, setMode] = useState("unit"); // "unit" | "roll"
  const [celebrate, setCelebrate] = useState(0);
  const [backorder, setBackorder] = useState(null);
  const [backorderDialogOpen, setBackorderDialogOpen] = useState(false);
  const [backorderPrefillItem, setBackorderPrefillItem] = useState(null);

  const load = async () => {
    const { data } = await api.get("/stock");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
  }, []);

  // Backorder hand-off — when launched from the Dashboard widget with
  // ?backorder_id=... preload the item + qty + supplier so the user just
  // confirms qty (or edits if partial) and taps Receive.
  useEffect(() => {
    const bid = searchParams.get("backorder_id");
    if (!bid || items.length === 0) return;
    api
      .get(`/backorders/${bid}`)
      .then(({ data }) => {
        setBackorder(data);
        const it = items.find((x) => x.id === data.stock_id);
        if (it) {
          setSelected(it);
          setQty(data.qty_expected || 1);
          if (data.supplier_name) setSupplier(data.supplier_name);
          if (data.po_ref || data.notes) {
            setNotes(
              [data.po_ref && `PO: ${data.po_ref}`, data.notes]
                .filter(Boolean)
                .join(" · "),
            );
          }
        }
      })
      .catch(() => {
        toast.error("Could not load backorder");
      });
  }, [searchParams, items]);

  /** After a successful receive, if the supplier the user typed differs from
   * the item's current default supplier, prompt them to set it. Only fires
   * when the typed name resolves to an existing supplier record — we don't
   * silently create suppliers as a side-effect of receiving. */
  const offerSetDefault = (item, typedSupplier) => {
    const typed = (typedSupplier || "").trim();
    if (!typed) return;
    const current = (item.supplier_default || "").trim();
    if (typed.toLowerCase() === current.toLowerCase()) return;
    const match = suppliers.find(
      (s) => s.name.trim().toLowerCase() === typed.toLowerCase(),
    );
    if (!match) return; // Typed name doesn't exist as a supplier record — skip.
    toast(`Set "${match.name}" as default supplier for ${item.name}?`, {
      duration: 12000,
      action: {
        label: "Set as default",
        onClick: async () => {
          try {
            const { data } = await api.put(`/stock/${item.id}`, {
              supplier_id: match.id,
            });
            setItems((arr) =>
              arr.map((x) => (x.id === item.id ? { ...x, ...data } : x)),
            );
            setSelected((cur) =>
              cur && cur.id === item.id ? { ...cur, ...data } : cur,
            );
            toast.success(`Default supplier updated`);
          } catch (e) {
            toast.error("Could not update default supplier");
          }
        },
      },
    });
  };

  // Staff are hard-filtered server-side to their assigned departments,
  // so the default chip stays on "all".

  const isRollItem = selected?.type === "roll" && Number(selected?.roll_length_m) > 0;
  const rollLen = Number(selected?.roll_length_m) || 0;

  useEffect(() => {
    if (selected) {
      // When launched from a backorder, the backorder-prefill effect has
      // already populated supplier/qty/notes for that exact item — don't
      // clobber them here. (For any later user-driven selection change we
      // reset as usual.)
      if (backorder && backorder.stock_id === selected.id) return;
      setSupplier(selected.supplier_default || "");
      // NOTE: batch ref is deliberately NOT reset here — a single delivery
      // usually covers many items with the same batch/PO number, so we keep
      // whatever the user typed. Tap the × next to the field to start fresh.
      setNotes("");
      setUnitCost(selected.unit_cost ? String(selected.unit_cost) : "");
      setIncludeProcessorFee(
        Number(selected?.default_processor_fee_per_log || 0) > 0,
      );
      setQty(1);
      setMode(selected.type === "roll" && Number(selected.roll_length_m) > 0 ? "roll" : "unit");
    }
  }, [selected, backorder]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (dept !== "all" && (it.department || "") !== dept) return false;
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      if (
        supplierFilter !== "all" &&
        (it.supplier_default || "") !== supplierFilter
      )
        return false;
      if (!q) return true;
      return (
        it.name.toLowerCase().includes(q) ||
        (it.sku || "").toLowerCase().includes(q) ||
        (it.supplier_default || "").toLowerCase().includes(q) ||
        (it.material || "").toLowerCase().includes(q) ||
        (it.colour || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, dept, material, category, supplierFilter]);

  const relevantMaterials = useMemo(() => {
    if (dept === "all") return [];
    const inScope = items.filter((it) => (it.department || "") === dept);
    return Array.from(new Set(inScope.map((it) => it.material).filter(Boolean)));
  }, [items, dept]);

  // Categories present in the currently-scoped items (respects dept +
  // material chips) so the row narrows as the user drills down.
  const relevantCategories = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all" && (it.department || "") !== dept) return false;
      if (material !== "all" && (it.material || "") !== material) return false;
      return true;
    });
    return Array.from(new Set(inScope.map((it) => it.category).filter(Boolean)));
  }, [items, dept, material]);

  // Suppliers present after Dept / Material / Category filters — so the chip
  // row narrows as the user drills down (e.g. picking "Milling" hides
  // suppliers who only ship to other departments).
  const relevantSuppliers = useMemo(() => {
    const inScope = items.filter((it) => {
      if (dept !== "all" && (it.department || "") !== dept) return false;
      if (material !== "all" && (it.material || "") !== material) return false;
      if (category !== "all" && (it.category || "") !== category) return false;
      return true;
    });
    return Array.from(
      new Set(inScope.map((it) => it.supplier_default).filter(Boolean)),
    );
  }, [items, dept, material, category]);

  const submit = async () => {
    if (!selected || qty <= 0) {
      toast.error("Pick an item and qty > 0");
      return;
    }
    // Zero-cost prompt: if the user left the cost blank or at $0 but the
    // Inventory item has a real unit_cost, offer to stamp it. Saves the
    // "silent $0 receive → wrong spend report" problem at the source.
    const inventoryCost = Number(selected?.unit_cost || 0);
    const enteredCost =
      unitCost === "" || unitCost === null || unitCost === undefined
        ? NaN
        : Number(unitCost);
    let effectiveUnitCost = enteredCost;
    if (
      inventoryCost > 0 &&
      (Number.isNaN(enteredCost) || enteredCost === 0)
    ) {
      const useIt = window.confirm(
        `Item cost is $${inventoryCost.toFixed(2)}/${selected.unit} on Inventory — apply it to this receive?\n\nTap OK to use $${inventoryCost.toFixed(2)}, or Cancel to record this receive at $0.`,
      );
      if (useIt) {
        effectiveUnitCost = inventoryCost;
        setUnitCost(String(inventoryCost));
      } else {
        effectiveUnitCost = 0;
      }
    }
    // Typo defence: if the new unit_cost is a huge jump from the last recorded
    // cost (>3x either way), pop a confirm before committing. This catches
    // decimal-shift mistakes like $57.25 → $5,725 that otherwise inflate
    // historical spend reports.
    const prev = Number(selected?.unit_cost || 0);
    const next = Number.isFinite(effectiveUnitCost) ? effectiveUnitCost : NaN;
    if (
      Number.isFinite(next) &&
      next > 0 &&
      prev > 0 &&
      (next / prev >= 3 || prev / next >= 3)
    ) {
      const dir = next > prev ? "up" : "down";
      const factor = Math.max(next / prev, prev / next).toFixed(1);
      const ok = window.confirm(
        `Heads-up — new cost $${next.toFixed(2)}/${selected.unit} is ${factor}× ${dir} from the last recorded $${prev.toFixed(2)}/${selected.unit}.\n\nIs this correct? (Tip: a common typo is $${(next / 10).toFixed(2)} vs $${next.toFixed(2)}.)`,
      );
      if (!ok) return;
    }
    const useRolls = mode === "roll" && isRollItem;
    const meters = useRolls ? Number(qty) * rollLen : Number(qty);
    const rollNote = useRolls
      ? `received ${qty} roll${Number(qty) === 1 ? "" : "s"} (${meters}${selected.unit})`
      : "";
    const composedNotes = [rollNote, notes].filter(Boolean).join(" · ");
    setBusy(true);
    try {
      const { data } = await api.post(`/stock/${selected.id}/receive`, {
        qty: meters,
        supplier,
        batch_ref: batch,
        notes: composedNotes,
        unit_cost: Number.isFinite(effectiveUnitCost)
          ? effectiveUnitCost
          : undefined,
        include_processor_fee: includeProcessorFee,
      });
      // Surface price changes (rebate or hike) so the user notices anomalies
      if (data?.price_change) {
        const pc = data.price_change;
        const pct = pc.delta_pct;
        if (pct !== null && pct !== undefined) {
          if (Math.abs(pct) >= 0.01) {
            const dir = pct > 0 ? "↑" : "↓";
            const tone = Math.abs(pct) >= 5 ? toast.warning : toast.info;
            tone(
              `Cost changed ${dir} ${Math.abs(pct)}% (was $${Number(pc.prev_unit_cost).toFixed(2)} → now $${Number(pc.unit_cost).toFixed(2)}/${selected.unit})`,
              { duration: 8000 }
            );
          }
        }
      }
      toast.success(
        useRolls
          ? `Received ${qty} roll${Number(qty) === 1 ? "" : "s"} (${meters}${selected.unit}) · ${selected.name}`
          : `Received ${qty} ${selected.unit} · ${selected.name}`
      );
      // Alert bell needs to know: low-stock badge may have cleared now that
      // qty is back above threshold.
      window.dispatchEvent(new CustomEvent("stock-updated"));
      // If launched from a backorder, close it now and drop the query param
      // so a refresh doesn't re-prefill.
      if (backorder?.id) {
        try {
          await api.put(`/backorders/${backorder.id}`, {
            status: "received",
            received_qty: meters,
          });
          toast.success("Backorder closed");
        } catch (_) {
          /* don't block the receive on a backorder-close failure */
        }
        setBackorder(null);
        setSearchParams({}, { replace: true });
      }
      // Prompt to set this supplier as the new default if it differs.
      offerSetDefault(selected, supplier);
      setCelebrate((c) => c + 1);
      setItems((arr) =>
        arr.map((x) =>
          x.id === selected.id ? { ...x, current_qty: data.item.current_qty, unit_cost: data.item.unit_cost } : x
        )
      );
      setSelected({ ...selected, current_qty: data.item.current_qty, unit_cost: data.item.unit_cost });
      setQty(1);
      // Keep the batch ref for the next item of the same delivery — user can
      // tap the × next to the field when they start a new delivery.
      setNotes("");
    } catch (e) {
      toast.error("Could not log incoming stock");
    } finally {
      setBusy(false);
    }
  };

  const handleScan = (code) => {
    setScanOpen(false);
    // Deep-link QR codes from /labels encode a URL with ?scan=<sku>.
    // Extract the SKU if the code is a URL; otherwise treat as raw.
    let payload = (code || "").trim();
    try {
      if (payload.includes("://") || payload.startsWith("/")) {
        const url = new URL(
          payload.startsWith("/") ? window.location.origin + payload : payload
        );
        payload = url.searchParams.get("scan") || payload;
      }
    } catch {
      /* not a URL — treat as raw SKU */
    }
    const codeLc = payload.trim().toLowerCase();
    const match =
      items.find((x) => x.id === payload.trim()) ||
      items.find((x) => (x.sku || "").toLowerCase() === codeLc) ||
      items.find((x) => (x.sku || "").toLowerCase().includes(codeLc)) ||
      items.find((x) => x.name.toLowerCase().includes(codeLc));
    if (match) {
      setSelected(match);
      toast.success(`Matched: ${match.name}`);
    } else {
      const isUuid = /^[0-9a-f-]{20,}$/i.test(payload.trim());
      const short = payload.length > 16 ? payload.slice(0, 12) + "…" : payload;
      const msg = isUuid
        ? `Label "${short}" doesn't match any current item — it may be from another environment or the item has been deleted.`
        : `No item matches "${payload}"`;
      toast.error(msg, { duration: 6000 });
      setSearch(isUuid ? "" : payload);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="receive-page">
      {backorder && (
        <div
          data-testid="receive-backorder-banner"
          className="nb-card p-3 mb-4 bg-[#FFE9C2] border-2 border-[#7A4A00] flex items-center gap-3"
        >
          <div className="label-eyebrow text-[#7A4A00]">Backorder</div>
          <div className="flex-1 text-sm">
            Receiving against backorder for{" "}
            <span className="font-bold">{backorder.stock_name}</span> —{" "}
            <span className="font-mono-num">
              {backorder.qty_expected} {backorder.stock_unit || ""}
            </span>
            {backorder.eta && (
              <span className="text-[#607087]"> · ETA {backorder.eta}</span>
            )}
          </div>
          <button
            data-testid="receive-backorder-clear"
            onClick={() => {
              setBackorder(null);
              setSearchParams({}, { replace: true });
              setSelected(null);
            }}
            className="nb-btn bg-white p-1.5"
            aria-label="clear backorder"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Log incoming deliveries</div>
          <h1 className="font-display text-5xl leading-none mt-2">Receive stock</h1>
        </div>
        <button
          data-testid="receive-scan"
          onClick={() => setScanOpen(true)}
          className="nb-btn nb-btn-dark px-5 py-3 flex items-center gap-2"
        >
          <ScanLine size={20} strokeWidth={2.5} /> Scan
        </button>
        <button
          data-testid="receive-log-backorder-btn"
          onClick={() => {
            setBackorderPrefillItem(selected || null);
            setBackorderDialogOpen(true);
          }}
          className="nb-btn bg-[#FFE9C2] border-[#7A4A00] px-5 py-3 flex items-center gap-2"
          title="Packing list shows something on backorder? Log it here."
        >
          <Hourglass size={18} strokeWidth={2.5} /> Log backorder
        </button>
      </div>

      {/* Open POs panel — click a PO to receive it against its packing
          slip. Above the manual pick-item flow because "closing the loop"
          from an existing PO is the preferred path when one exists. */}
      {openPos.length > 0 && (
        <div
          className="nb-card p-5 mb-5 bg-[#EAF5FB] border-[#0A3A7A]"
          data-testid="receive-open-pos-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Truck size={18} strokeWidth={2.5} className="text-[#0A3A7A]" />
            <h2 className="font-display text-xl">Open purchase orders</h2>
            <span className="text-xs text-[#607087]">
              · {openPos.length} awaiting delivery
            </span>
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
            data-testid="receive-open-pos-grid"
          >
            {openPos.map((p) => {
              const outstanding = (p.items || []).reduce(
                (s, it) =>
                  s +
                  Math.max(0, Number(it.qty || 0) - Number(it.qty_received || 0)),
                0,
              );
              const partial = p.status === "partially_received";
              const daysSince = p.sent_at
                ? Math.floor(
                    (Date.now() - new Date(p.sent_at)) / 86400000,
                  )
                : null;
              // v184 — Actual date/time of when the PO was sent + set of
              // unique departments across the PO's line items (looked up
              // from the loaded stock catalog so the card can show a thin
              // coloured bar per dept).
              const sentDate = p.sent_at
                ? new Date(p.sent_at).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : null;
              const deptSet = new Set();
              (p.items || []).forEach((it) => {
                const stock = items.find((x) => x.id === it.stock_id);
                if (!stock) return;
                const list =
                  Array.isArray(stock.departments) && stock.departments.length
                    ? stock.departments
                    : stock.department
                      ? [stock.department]
                      : [];
                list.forEach((d) => deptSet.add(d));
              });
              const deptList = Array.from(deptSet);
              return (
                <div
                  key={p.id}
                  data-testid={`receive-open-po-${p.id}`}
                  className="relative border-2 border-black rounded-xl overflow-hidden bg-white hover:bg-[#F0F7FB] transition-colors"
                >
                  {/* v184 — Thin coloured strip per unique dept across
                      the line items. Tells the receiver at a glance which
                      team is waiting on this delivery. */}
                  {deptList.length > 0 && (
                    <div
                      className="flex h-1.5 w-full"
                      data-testid={`receive-open-po-${p.id}-dept-strip`}
                    >
                      {deptList.map((d) => (
                        <div
                          key={d}
                          style={{ background: colorFor(d) }}
                          className="flex-1"
                          title={d}
                        />
                      ))}
                    </div>
                  )}
                  <div className="p-3">
                    <button
                      type="button"
                      onClick={() => setReceiveFromPo(p)}
                      className="text-left w-full pr-7"
                      data-testid={`receive-open-po-${p.id}-open`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-base">{p.po_number}</span>
                        {partial && (
                          <span className="text-[9px] font-black tracking-wider uppercase bg-[#FFF3D4] border border-[#7A5A00] text-[#3B2A00] rounded px-1 py-0.5">
                            Part received
                          </span>
                        )}
                        {deptList.map((d) => (
                          <DepartmentBadge key={d} department={d} />
                        ))}
                      </div>
                      <div className="text-xs text-[#607087] truncate">
                        {p.supplier_snapshot?.name || "No supplier"}
                      </div>
                      <div className="text-[11px] font-bold mt-1">
                        <span className="font-mono-num">{outstanding}</span> unit
                        {outstanding === 1 ? "" : "s"} outstanding · ${Number(p.total || 0).toFixed(0)}
                      </div>
                      {sentDate && (
                        <div
                          className="text-[10px] text-[#607087] font-normal mt-0.5"
                          data-testid={`receive-open-po-${p.id}-timestamp`}
                        >
                          Sent {sentDate}
                          {daysSince != null && daysSince > 0 && (
                            <span> · {daysSince}d ago</span>
                          )}
                        </div>
                      )}
                    </button>
                    {/* v181 — Cancel-this-PO button. Opens ClosePODialog for
                        reason selection (manually received / supplier
                        cancelled / duplicate / other). No stock or cost
                        changes. Renamed from "Close" → visual "Cancel" cue
                        because staff confused Close-the-dialog vs Close-the-PO. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClosePo(p);
                      }}
                      data-testid={`receive-open-po-${p.id}-dismiss`}
                      className="absolute top-2 right-2 p-1 rounded-md hover:bg-[#FFE4E4] text-[#607087] hover:text-[#B0272D]"
                      title="Cancel this PO (manually received, supplier cancelled, duplicate) — no stock/cost changes"
                      aria-label="Cancel this PO"
                    >
                      <XCircle size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="nb-card p-5">
          <h2 className="font-display text-xl mb-3">1 · Pick item</h2>
          <div className="mb-3">
            <DepartmentChips value={dept} onChange={setDept} testidPrefix="receive-dept" />
          </div>
          <div className="mb-3">
            <MaterialChips
              value={material}
              onChange={setMaterial}
              testidPrefix="receive-mat"
              relevantNames={relevantMaterials}
            />
          </div>
          <div className="mb-3">
            <CategoryChips
              value={category}
              onChange={setCategory}
              testidPrefix="receive-cat"
              relevantNames={relevantCategories}
            />
          </div>
          <div className="mb-3">
            <SupplierChips
              value={supplierFilter}
              onChange={setSupplierFilter}
              testidPrefix="receive-supplier"
              relevantNames={relevantSuppliers}
            />
          </div>
          <div className="relative mb-3">
            <Search
              size={18}
              strokeWidth={2.5}
              className="absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              data-testid="receive-search-input"
              className="nb-input pl-11 h-12"
              placeholder="Search catalog…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div
            className="flex flex-col gap-2 max-h-[480px] overflow-y-auto scrollbar-clean pr-2"
            data-testid="receive-item-list"
          >
            {filtered.map((it) => (
              <button
                key={it.id}
                data-testid={`receive-pick-${it.id}`}
                onClick={() => setSelected(it)}
                className="flex items-center gap-3 p-2 border-2 rounded-xl text-left transition-all"
                style={{
                  borderColor: selected?.id === it.id ? "#5FBE7E" : "#001740",
                  background: selected?.id === it.id ? "#E7F7EE" : "#fff",
                  boxShadow:
                    selected?.id === it.id ? "3px 3px 0 #5FBE7E" : "2px 2px 0 #001740",
                }}
              >
                <StockThumb item={it} size="h-12 w-12" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{it.name}</div>
                  <div className="label-eyebrow">
                    {it.type} · {it.size_spec || it.sku || ""}
                  </div>
                </div>
                <div className="font-mono-num font-bold">
                  {fmtQty(it.current_qty)} {it.unit}
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-sm text-[#6B6B6B] text-center py-6">
                No items match.
              </div>
            )}
          </div>
        </div>

        <div className="nb-card p-5" data-testid="receive-form">
          <h2 className="font-display text-xl mb-3">2 · Delivery details</h2>
          {!selected ? (
            <div className="text-[#6B6B6B] text-sm py-10 text-center">
              Pick an item on the left, or tap Scan to use the camera.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 border-2 border-black rounded-2xl p-3 bg-[#E7F7EE]">
                <StockThumb item={selected} size="h-14 w-14" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{selected.name}</div>
                  <div className="label-eyebrow">
                    current: {fmtQty(selected.current_qty)} {selected.unit}
                  </div>
                </div>
                <button
                  data-testid="receive-clear-selection"
                  onClick={() => setSelected(null)}
                  className="nb-btn bg-white p-2"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              </div>

              {isRollItem && (
                <div className="flex items-center gap-2" data-testid="receive-mode-toggle">
                  <div className="label-eyebrow">Receive as</div>
                  <div className="flex gap-1 p-1 rounded-xl bg-white/70 border border-white">
                    <button
                      data-testid="receive-mode-roll"
                      onClick={() => {
                        setMode("roll");
                        setQty(1);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg font-display transition ${
                        mode === "roll"
                          ? "bg-[#228EBF] text-white shadow-sm"
                          : "text-[#001740] hover:bg-white"
                      }`}
                    >
                      Whole rolls
                    </button>
                    <button
                      data-testid="receive-mode-unit"
                      onClick={() => {
                        setMode("unit");
                        setQty(1);
                      }}
                      className={`px-3 py-1.5 text-sm rounded-lg font-display transition ${
                        mode === "unit"
                          ? "bg-[#228EBF] text-white shadow-sm"
                          : "text-[#001740] hover:bg-white"
                      }`}
                    >
                      Loose {selected.unit}
                    </button>
                  </div>
                  <div className="label-eyebrow text-[#607087] ml-auto">
                    1 roll = {rollLen}{selected.unit}
                  </div>
                </div>
              )}

              <label>
                <div className="label-eyebrow mb-1">
                  Qty received *{" "}
                  {isRollItem && mode === "roll" && (
                    <span className="text-[#607087]">
                      · {(Number(qty) || 0) * rollLen}
                      {selected.unit} total
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="qty-minus"
                    onClick={() => setQty((q) => Math.max(0, Number(q) - 1))}
                    className="nb-btn bg-white p-3"
                  >
                    <Minus size={18} strokeWidth={2.5} />
                  </button>
                  <input
                    data-testid="receive-qty-input"
                    className="nb-input font-mono-num text-2xl text-center"
                    type="number"
                    step={isRollItem && mode === "roll" ? "1" : "0.01"}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                  <button
                    data-testid="qty-plus"
                    onClick={() => setQty((q) => Number(q) + 1)}
                    className="nb-btn bg-white p-3"
                  >
                    <Plus size={18} strokeWidth={2.5} />
                  </button>
                  <div className="font-mono-num font-bold text-lg px-3 py-2 border-2 border-black rounded-md bg-white">
                    {isRollItem && mode === "roll"
                      ? Number(qty) === 1
                        ? "roll"
                        : "rolls"
                      : selected.unit}
                  </div>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {(isRollItem && mode === "roll"
                    ? [1, 2, 3, 5, 10]
                    : [5, 10, 25, 50, 100]
                  ).map((n) => (
                    <button
                      key={n}
                      data-testid={`qty-preset-${n}`}
                      onClick={() => setQty(n)}
                      className="nb-btn bg-white px-3 py-1 text-sm"
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </label>

              {/* Cost calculator — enter ANY one of Total / Per {unit} /
                  Per {pack_unit}; the other two auto-fill. Solves the
                  "supplier bills me for the bulk but I stock in drums"
                  problem so the manager never has to divide $9,000 ÷ 7.5
                  in their head on receiving day. */}
              {(() => {
                const packSize = Number(selected.pack_size) || 0;
                const packUnit = selected.pack_unit || "";
                const hasPack = packSize > 0 && !!packUnit;
                const qNum = Number(qty) || 0;
                const uc = Number(unitCost) || 0;
                const totalCost = qNum > 0 && uc > 0 ? qNum * uc : 0;
                const perSub = hasPack && uc > 0 ? uc / packSize : 0;

                const setFromTotal = (v) => {
                  const t = Number(v);
                  if (qNum > 0 && !Number.isNaN(t)) {
                    setUnitCost((t / qNum).toFixed(4));
                  } else if (v === "") {
                    setUnitCost("");
                  }
                };
                const setFromPerPack = (v) => setUnitCost(v);
                const setFromPerSub = (v) => {
                  const s = Number(v);
                  if (packSize > 0 && !Number.isNaN(s)) {
                    setUnitCost((s * packSize).toFixed(4));
                  } else if (v === "") {
                    setUnitCost("");
                  }
                };

                const displayTotal =
                  costFocus === "total"
                    ? costDraft
                    : totalCost > 0
                    ? totalCost.toFixed(2)
                    : "";
                const displayPerPack =
                  costFocus === "perPack"
                    ? costDraft
                    : uc > 0
                    ? uc.toFixed(2)
                    : "";
                const displayPerSub =
                  costFocus === "perSub"
                    ? costDraft
                    : perSub > 0
                    ? perSub.toFixed(4)
                    : "";
                const onFocus = (which, initial) => () => {
                  setCostFocus(which);
                  setCostDraft(String(initial ?? ""));
                };
                const onBlur = () => {
                  setCostFocus(null);
                  setCostDraft("");
                };
                const onChange = (which, setter) => (e) => {
                  setCostDraft(e.target.value);
                  if (costFocus !== which) setCostFocus(which);
                  setter(e.target.value);
                };

                return (
                  <div className="nb-card-sm p-3 bg-[#F7F5EE]">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="label-eyebrow">
                        Cost on this delivery — fill any one
                      </div>
                      {selected.unit_cost > 0 && (
                        <div className="text-[10px] text-[#607087]">
                          last: ${Number(selected.unit_cost).toFixed(2)}/{selected.unit}
                        </div>
                      )}
                    </div>
                    <div
                      className={`grid gap-2 ${hasPack ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
                    >
                      <label>
                        <div className="label-eyebrow mb-1">Total invoice</div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                          <input
                            data-testid="receive-total-cost-input"
                            type="number"
                            step="0.01"
                            className="nb-input font-mono-num pl-7"
                            value={displayTotal}
                            onFocus={onFocus("total", totalCost > 0 ? totalCost.toFixed(2) : "")}
                            onBlur={onBlur}
                            onChange={onChange("total", setFromTotal)}
                            placeholder="0.00"
                            disabled={qNum <= 0}
                          />
                        </div>
                        <div className="label-eyebrow mt-1 text-[#607087]">
                          for {qNum || 0} {selected.unit}{qNum === 1 ? "" : "s"}
                        </div>
                      </label>
                      <label>
                        <div className="label-eyebrow mb-1">
                          Per {selected.unit}
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                          <input
                            data-testid="receive-unit-cost-input"
                            type="number"
                            step="0.01"
                            className="nb-input font-mono-num pl-7"
                            value={displayPerPack}
                            onFocus={onFocus("perPack", unitCost ?? "")}
                            onBlur={onBlur}
                            onChange={onChange("perPack", setFromPerPack)}
                            placeholder={selected.unit_cost ? `${Number(selected.unit_cost).toFixed(2)} (last)` : "0.00"}
                          />
                        </div>
                        <div className="label-eyebrow mt-1 text-[#607087]">
                          price of one {selected.unit}
                        </div>
                      </label>
                      {hasPack && (
                        <label>
                          <div className="label-eyebrow mb-1">
                            Per {packUnit}
                          </div>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-num text-[#607087]">$</span>
                            <input
                              data-testid="receive-per-sub-cost-input"
                              type="number"
                              step="0.0001"
                              className="nb-input font-mono-num pl-7"
                              value={displayPerSub}
                              onFocus={onFocus("perSub", perSub > 0 ? perSub.toFixed(4) : "")}
                              onBlur={onBlur}
                              onChange={onChange("perSub", setFromPerSub)}
                              placeholder="0.0000"
                            />
                          </div>
                          <div className="label-eyebrow mt-1 text-[#607087]">
                            1 {selected.unit} = {packSize} {packUnit}
                          </div>
                        </label>
                      )}
                    </div>
                    {Number(unitCost) > 0 &&
                      Number(selected.unit_cost) > 0 &&
                      Math.abs(Number(unitCost) - Number(selected.unit_cost)) > 0.005 && (
                        <div
                          data-testid="cost-change-hint"
                          className="text-xs mt-2 font-medium"
                          style={{
                            color:
                              Number(unitCost) > Number(selected.unit_cost)
                                ? "#E5263F"
                                : "#4DA86A",
                          }}
                        >
                          {Number(unitCost) > Number(selected.unit_cost) ? "↑" : "↓"}{" "}
                          {Math.abs(
                            (100 * (Number(unitCost) - Number(selected.unit_cost))) /
                              Number(selected.unit_cost),
                          ).toFixed(1)}
                          % vs last delivery
                        </div>
                      )}
                    {Number(selected?.default_processor_fee_per_log || 0) > 0 && (() => {
                      const fee = Number(selected.default_processor_fee_per_log);
                      const perWheel = fee / 10;
                      const base = Number(unitCost) || 0;
                      const landed = base + (includeProcessorFee ? perWheel : 0);
                      return (
                        <label
                          className="mt-3 flex items-start gap-2 border-2 border-black rounded-lg p-2.5 bg-[#FFF8E1]"
                          data-testid="receive-include-processor-fee"
                        >
                          <input
                            type="checkbox"
                            checked={includeProcessorFee}
                            onChange={(e) => setIncludeProcessorFee(e.target.checked)}
                            className="w-5 h-5 mt-0.5"
                          />
                          <div className="flex-1 text-xs">
                            <div className="font-bold text-[#3B2A00]">
                              Include cutting fee (+${perWheel.toFixed(2)}/{selected.unit})
                            </div>
                            <div className="text-[#7A5300] mt-0.5">
                              ${fee.toFixed(2)}/log ÷ 10 wheels — rolls into
                              this receive's unit cost so consumption reports
                              show true landed cost.{" "}
                              <b className="whitespace-nowrap">
                                Landed: ${landed.toFixed(2)}/{selected.unit}
                              </b>
                            </div>
                          </div>
                        </label>
                      );
                    })()}
                  </div>
                );
              })()}

              <label>
                <div className="label-eyebrow mb-1">Supplier</div>
                <input
                  data-testid="receive-supplier-input"
                  className="nb-input"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  list="receive-supplier-options"
                  autoComplete="off"
                  placeholder={
                    suppliers.length ? "Start typing — picks from your suppliers" : ""
                  }
                />
                <datalist id="receive-supplier-options">
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name} />
                  ))}
                </datalist>
              </label>

              <label>
                <div className="label-eyebrow mb-1 flex items-center gap-2">
                  <span>Batch / PO ref</span>
                  {batch && (
                    <span
                      className="text-[10px] font-bold text-[#0B6B2A] bg-[#DFF7E4] border border-[#0B6B2A] rounded-full px-1.5 py-0.5"
                      data-testid="receive-batch-sticky-tag"
                    >
                      kept for this delivery
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    data-testid="receive-batch-input"
                    className="nb-input pr-10"
                    value={batch}
                    onChange={(e) => setBatch(e.target.value)}
                    placeholder="Type once — reused for every item on this delivery"
                  />
                  {batch && (
                    <button
                      type="button"
                      data-testid="receive-batch-clear"
                      onClick={() => setBatch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 nb-btn bg-white p-1"
                      title="Clear batch — start a new delivery"
                      aria-label="Clear batch ref"
                    >
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </label>

              <label>
                <div className="label-eyebrow mb-1">Notes</div>
                <textarea
                  data-testid="receive-notes-input"
                  className="nb-input"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>

              <button
                data-testid="receive-submit"
                onClick={submit}
                disabled={busy}
                className="nb-btn nb-btn-success py-4 text-lg flex items-center justify-center gap-2"
              >
                <PackagePlus size={20} strokeWidth={2.5} /> Log received
              </button>
            </div>
          )}
        </div>
      </div>

      <QrScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleScan}
      />

      <ReceiveCelebration trigger={celebrate} />
      <BackorderFormDialog
        open={backorderDialogOpen}
        lockedStockId={backorderPrefillItem?.id || null}
        onClose={() => {
          setBackorderDialogOpen(false);
          setBackorderPrefillItem(null);
        }}
        onSaved={() => {
          setBackorderDialogOpen(false);
          setBackorderPrefillItem(null);
        }}
      />
      <ReceiveFromPODialog
        open={!!receiveFromPo}
        po={receiveFromPo}
        onClose={() => setReceiveFromPo(null)}
        onReceived={() => {
          loadOpenPos();
          load();
        }}
      />
      <ClosePODialog
        open={!!closePo}
        po={closePo}
        onClose={() => setClosePo(null)}
        onClosed={() => {
          loadOpenPos();
          window.dispatchEvent(new Event("stock-updated"));
        }}
      />
    </div>
  );
};

export default Receive;
